import { useEffect, useRef } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Programmatic atmospherics: starfield canvas + noise + scanlines + faint grid.
 * Fixed, non-interactive, sits behind all content.
 */
export const Atmosphere = ({ stars = true }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!stars) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = prefersReducedMotion();
    let width = 0;
    let height = 0;
    let points = [];
    let running = true;

    const build = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = width < 640 ? 70 : 150;
      points = Array.from({ length: target }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 1.1 + 0.25,
        a: Math.random() * 0.5 + 0.12,
        s: Math.random() * 0.0012 + 0.0004,
        p: Math.random() * Math.PI * 2
      }));
    };

    const draw = (t) => {
      if (!running) return;
      ctx.clearRect(0, 0, width, height);
      points.forEach((pt) => {
        const twinkle = reduced ? 1 : 0.6 + 0.4 * Math.sin(pt.p + t * pt.s);
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, pt.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(226, 238, 245, ${pt.a * twinkle})`;
        ctx.fill();
      });
      if (!reduced) rafRef.current = requestAnimationFrame(draw);
    };

    build();
    if (reduced) {
      draw(0);
    } else {
      rafRef.current = requestAnimationFrame(draw);
    }

    const onResize = () => {
      build();
      if (reduced) draw(0);
    };
    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      } else if (!reduced) {
        running = true;
        rafRef.current = requestAnimationFrame(draw);
      }
    };

    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [stars]);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
      data-testid="atmosphere-layer"
    >
      <div className="absolute inset-0 bg-background" />
      {stars && (
        <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      )}
      <div className="fx-grid absolute inset-0 opacity-[0.35]" />
      <div
        className="fx-scanlines absolute inset-0"
        style={{ opacity: "var(--scanline-opacity)" }}
      />
      <div
        className="fx-noise absolute inset-0 mix-blend-overlay"
        style={{ opacity: "var(--noise-opacity)" }}
      />
      <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-background to-transparent" />
    </div>
  );
};

/** The Threadway anchor: a dark geometric mass with teal edges and amber rim light. */
export const ThreadwayAnchor = ({ className = "" }) => (
  <svg
    viewBox="0 0 420 420"
    className={className}
    aria-hidden="true"
    role="presentation"
  >
    <defs>
      <radialGradient id="tw-glow" cx="28%" cy="22%" r="78%">
        <stop offset="0%" stopColor="hsl(32 92% 62%)" stopOpacity="0.34" />
        <stop offset="62%" stopColor="hsl(32 92% 62%)" stopOpacity="0.04" />
        <stop offset="100%" stopColor="hsl(32 92% 62%)" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="tw-face" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="hsl(220 16% 14%)" />
        <stop offset="100%" stopColor="hsl(220 20% 7%)" />
      </linearGradient>
    </defs>

    <circle cx="210" cy="210" r="205" fill="url(#tw-glow)" />

    {/* orbit rings */}
    <circle
      cx="210"
      cy="210"
      r="178"
      fill="none"
      stroke="hsl(174 72% 42%)"
      strokeOpacity="0.18"
      strokeWidth="1"
      strokeDasharray="3 9"
    />
    <circle
      cx="210"
      cy="210"
      r="144"
      fill="none"
      stroke="hsl(210 20% 96%)"
      strokeOpacity="0.07"
      strokeWidth="1"
    />

    {/* anchor body */}
    <g>
      <polygon
        points="210,54 336,138 336,282 210,366 84,282 84,138"
        fill="url(#tw-face)"
        stroke="hsl(174 72% 42%)"
        strokeOpacity="0.55"
        strokeWidth="1.25"
      />
      <polygon
        points="210,104 292,160 292,262 210,318 128,262 128,160"
        fill="none"
        stroke="hsl(210 20% 96%)"
        strokeOpacity="0.12"
        strokeWidth="1"
      />
      <polygon
        points="210,150 254,180 254,242 210,272 166,242 166,180"
        fill="hsl(220 20% 5%)"
        stroke="hsl(32 92% 62%)"
        strokeOpacity="0.4"
        strokeWidth="1"
      />
      {/* rim light */}
      <path
        d="M210,54 L336,138"
        stroke="hsl(32 92% 62%)"
        strokeOpacity="0.62"
        strokeWidth="2"
      />
      <path
        d="M84,138 L210,54"
        stroke="hsl(32 92% 62%)"
        strokeOpacity="0.3"
        strokeWidth="1.5"
      />
      {/* internal structure */}
      <path
        d="M210,54 L210,150 M84,138 L166,180 M336,138 L254,180 M84,282 L166,242 M336,282 L254,242 M210,366 L210,272"
        stroke="hsl(174 72% 42%)"
        strokeOpacity="0.22"
        strokeWidth="1"
      />
    </g>

    {/* the six markers — one is missing */}
    {[0, 1, 2, 3, 4, 5].map((i) => {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      const cx = 210 + Math.cos(angle) * 178;
      const cy = 210 + Math.sin(angle) * 178;
      const missing = i === 3;
      return (
        <g key={i}>
          <circle
            cx={cx}
            cy={cy}
            r={missing ? 4 : 3}
            fill={missing ? "none" : "hsl(174 72% 52%)"}
            stroke={missing ? "hsl(2 72% 56%)" : "none"}
            strokeWidth="1"
            strokeDasharray={missing ? "2 2" : "0"}
            opacity={missing ? 0.9 : 0.75}
          />
        </g>
      );
    })}
  </svg>
);
